import {apiLink} from '../Common/common';
import i18n from '../i18n';
/**
 * Streaming chat client for React Native using XMLHttpRequest.
 * Streams plain text from the backend (no SSE framing).
 * Reuses the pattern from sseClient.ts but simplified for chat endpoint.
 */

export interface StreamChatResponse {
  promise: Promise<string>;
  cancel: () => void;
}

/** A photo picked with react-native-image-picker (file URI on the device). */
export interface BillPhotoFile {
  uri: string;
  type: string;
  name: string;
}

/**
 * Shared XHR plumbing for both chat endpoints: opens the request, hands the body to `send`, and slices the growing
 * responseText into pieces for onChunk. A 4xx/5xx response is NOT chat text (it is a JSON error body), so it rejects
 * instead of being shown as a reply.
 */
function runStream(
  url: string,
  token: string,
  send: (xhr: XMLHttpRequest) => void,
  onChunk: (piece: string) => void,
  timeoutMs = 0,
): StreamChatResponse {
  let xhr: XMLHttpRequest | null = null;
  let seenLength = 0;
  let resolved = false;
  let resolvePromise: (value: string) => void;
  let rejectPromise: (error: unknown) => void;

  const promise = new Promise<string>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  try {
    xhr = new XMLHttpRequest();
    seenLength = 0;
    xhr.open('POST', url, true);
    if (timeoutMs > 0) xhr.timeout = timeoutMs;
    xhr.setRequestHeader('auth', token);

    xhr.onreadystatechange = () => {
      if (!xhr) return;

      // On both partial (3) and complete (4) state, process available text
      if (xhr.readyState === 3 || xhr.readyState === 4) {
        // A 401/403/429 body is JSON, not chat text - fail instead of showing it as a reply.
        if (xhr.status >= 400) {
          if (xhr.readyState === 4 && !resolved) {
            resolved = true;
            let message = `Chat request failed with status ${xhr.status}`;
            try {
              const j = JSON.parse(xhr.responseText || '{}');
              if (j && typeof j.message === 'string') message = j.message;
            } catch {}
            rejectPromise(new Error(message));
          }
          return;
        }
        const fullText = xhr.responseText || '';
        const delta = fullText.slice(seenLength);
        seenLength = fullText.length;

        if (delta) {
          onChunk(delta);
        }

        // When done, resolve with accumulated text
        if (xhr.readyState === 4 && !resolved) {
          resolved = true;
          resolvePromise(fullText);
        }
      }
    };

    xhr.onerror = (err) => {
      if (!resolved) {
        resolved = true;
        rejectPromise(err);
      }
    };

    xhr.ontimeout = () => {
      if (!resolved) {
        resolved = true;
        rejectPromise(new Error('Chat request timed out'));
      }
    };

    xhr.onabort = () => {
      if (!resolved) {
        resolved = true;
        rejectPromise(new Error('Chat request aborted'));
      }
    };

    send(xhr);
  } catch (err) {
    if (!resolved) {
      resolved = true;
      rejectPromise(err);
    }
  }

  const cancel = () => {
    if (xhr) {
      try {
        xhr.abort();
      } catch {}
      xhr = null;
    }
  };

  return { promise, cancel };
}

/**
 * Stream a chat message from the backend, calling onChunk with each received piece.
 * Returns a promise that resolves with the complete accumulated text.
 * @param message - The user's question
 * @param token - JWT auth token
 * @param assistantName - Name of the AI assistant
 * @param onChunk - Callback fired when a new text chunk arrives
 * @param baseUrl - Base URL for the API (e.g. 'https://api.bluehms.com/api/')
 * @returns Promise + cancel function for cleanup
 */
export function streamChat(
  message: string,
  token: string,
  assistantName: string,
  onChunk: (piece: string) => void,
  baseUrl: string = apiLink,
): StreamChatResponse {
  return runStream(
    `${baseUrl}chatbot/user/stream`,
    token,
    (xhr) => {
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify({ message, assistantName, language: (i18n.language || 'en').slice(0, 2) }));
    },
    onChunk,
  );
}

/**
 * Send a photo of a purchase bill. The server reads it (20-120 s: it sends a status line and dots meanwhile) and
 * streams back a confirm card. Multipart over XHR: do NOT set Content-Type, React Native adds the boundary itself.
 */
export function streamBillPhoto(
  file: BillPhotoFile,
  caption: string,
  token: string,
  assistantName: string,
  onChunk: (piece: string) => void,
  baseUrl: string = apiLink,
): StreamChatResponse {
  return runStream(
    `${baseUrl}chatbot/bill/stream`,
    token,
    (xhr) => {
      const form = new FormData();
      form.append('image', { uri: file.uri, type: file.type || 'image/jpeg', name: file.name || 'bill.jpg' } as any);
      form.append('caption', caption);
      form.append('assistantName', assistantName);
      xhr.send(form);
    },
    onChunk,
    240000,
  );
}
